import React from 'react';
import { iconPaths, type IconName, type IconEntry } from './registry';
import { useDensity } from '@liftango/ui-tokens';

export interface IconProps extends Omit<React.SVGAttributes<SVGSVGElement>, 'd'> {
  /** Name from the FA-derived registry. Mutually exclusive with `d`. */
  name?: IconName;
  /** Inline path override (escape hatch for ad-hoc icons). Uses a 0 0 24 24 viewBox. */
  d?: string | string[];
  /** Pixel size; falls back to density.iconSize. */
  size?: number;
  /** Fill color for the path(s); defaults to currentColor. */
  fill?: string;
}

const FALLBACK_VIEWBOX = '0 0 24 24';

function resolve(name: IconName | undefined, dOverride: IconProps['d']): IconEntry | null {
  if (dOverride) return { d: dOverride, viewBox: FALLBACK_VIEWBOX };
  if (name && iconPaths[name]) return iconPaths[name];
  return null;
}

export const Icon = React.forwardRef<SVGSVGElement, IconProps>(function Icon(
  { name, d, size, fill = 'currentColor', 'aria-label': ariaLabel, ...rest },
  ref,
) {
  const density = useDensity();
  const resolved = resolve(name, d);
  if (!resolved) return null;

  const resolvedSize = size ?? density.iconSize;
  const isLabelled = !!ariaLabel;
  const paths = typeof resolved.d === 'string' ? [resolved.d] : resolved.d;

  return (
    <svg
      ref={ref}
      width={resolvedSize}
      height={resolvedSize}
      viewBox={resolved.viewBox}
      aria-hidden={isLabelled ? undefined : 'true'}
      aria-label={ariaLabel}
      role={isLabelled ? 'img' : undefined}
      {...rest}
    >
      {paths.map((p, i) => (
        <path key={i} d={p} fill={fill} />
      ))}
    </svg>
  );
});
